import { ContentType } from 'types/publishedAssets';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import { BlockHeading } from 'components/Content/Atoms/BlockHeading';
import { ContentContainer } from 'components/Content/Atoms/ContentContainer';
import styles from './IFrameContent.module.scss';

interface Props {
  content: ContentType;
}

const SIZE_HEIGHTS: Record<string, number> = {
  small: 300,
  medium: 500,
  large: 700,
  full: 900,
};

function parseSrcFromCodeSnippet(codeSnippet: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(codeSnippet, 'text/html');
    const iframe = doc.querySelector('iframe');
    const src = iframe?.getAttribute('src');
    if (!src || /^javascript:/i.test(src.trim())) return null;
    return src;
  } catch {
    return null;
  }
}

export function IFrameContent(props: Props) {
  const { content } = props;
  const { translate } = useGetContentTranslations();
  const label = translate(content.translations, 'label');
  const secondaryText = translate(content.translations, 'description');
  const codeSnippet = content.metadata.code_snippet || '';
  const size = content.metadata.size || 'small';
  const height = SIZE_HEIGHTS[size] ?? 300;
  const src = codeSnippet ? parseSrcFromCodeSnippet(codeSnippet) : null;

  return (
    <ContentContainer content={content}>
      <BlockHeading title={label} secondaryText={secondaryText} />
      {src ? (
        <div className={styles.iframeContainer} style={{ height }}>
          <iframe
            src={src}
            title={label || 'Embedded content'}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            className={styles.iframe}
            allowFullScreen
          />
        </div>
      ) : (
        <div className={`${styles.iframeContainer} ${styles.fallback}`} style={{ height }}>
          <span>Content not available</span>
        </div>
      )}
    </ContentContainer>
  );
}
